import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import PuffLoader from "react-spinners/PuffLoader";
import "./redirect.scss";

const publicUrlSandbox = "https://sandbox.paydeca.com";
const publicUrlProduction = "https://api.paydeca.com";
const publicUrlDev = "https://dev.veripaygate.com/api";

const publicUrl = {
  prod: publicUrlProduction,
  dev: publicUrlDev,
  sandbox: publicUrlSandbox,
};

const POLL_INTERVAL_MS = 2000;

const REQUEST_TIMEOUT_MS = 5000;

const MAX_ATTEMPTS = 15;

function Redirect() {
  const { env, txid } = useParams();
  const [searchParams] = useSearchParams();
  const [failed, setFailed] = useState(false);

  const settling = searchParams.get("settling") === "1";

  useEffect(() => {
    if (!txid) {
      setFailed(true);
      return;
    }

    const base = publicUrl[env] || publicUrlSandbox;
    const eventsUrl =
      `${base}/fn-execute/payment/3dsv-events/${txid}` + (settling ? "?settling=1" : "");

    let settled = false;
    let stopped = false;
    let timer = null;
    let inFlight = null;

    const settle = (url) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (inFlight) inFlight.abort();
      if (url) window.location.replace(url);
      else setFailed(true);
    };

    let fallbackUrl = null;
    let attempts = 0;

    const consume = ({ event, payload } = {}) => {
      const data = payload || {};
      if (event === "redirect" || event === "acquirer") settle(data.url);
      else if (event === "invalid") settle(null);
      else if (data.fallbackUrl) fallbackUrl = data.fallbackUrl;
    };

    const poll = async () => {
      const attempt = new AbortController();
      inFlight = attempt;
      const guard = setTimeout(() => attempt.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(eventsUrl, {
          signal: attempt.signal,
          cache: "no-store",
          headers: { Accept: "application/json" },
        });

        // The server answered — the rest of the read is no longer on the clock.
        clearTimeout(guard);

        if (!response.ok) return;
        consume(await response.json());
      } finally {
        clearTimeout(guard);
        if (inFlight === attempt) inFlight = null;
      }
    };

    const tick = () => {
      attempts += 1;
      poll()
        .catch(() => {})
        .then(() => {
          if (settled || stopped) return;
          if (attempts >= MAX_ATTEMPTS) return settle(fallbackUrl);
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        });
    };

    tick();

    return () => {
      stopped = true;
      clearTimeout(timer);
      if (inFlight) inFlight.abort();
    };
  }, [env, txid, settling]);

  return (
    <div className="redirect">
      <div className="container">
        {failed ? (
          <div className="body">
            <p className="title">Payment could not be completed</p>
            <p className="detail">
              We could not confirm this transaction. Please return to the store and try again.
            </p>
          </div>
        ) : (
          <div className="body">
            <PuffLoader color="#36d7b7" />
            <p className="title">Confirming your payment</p>
            <p className="detail">
              This takes a few seconds. Please do not close this page or press back.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Redirect;
